import { Layout } from "@/components/layout/Layout";
import { usePageMeta } from "@/hooks/use-page-meta";
import { projects } from "@/data/projects";
import { useRoute } from "wouter";
import NotFound from "@/pages/not-found";
import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { ArrowLeft, ArrowRight } from "lucide-react";

export default function WorkDetail() {
  const [match, params] = useRoute("/work/:slug");
  
  if (!match || !params?.slug) {
    return <NotFound />;
  }

  const project = projects.find(p => p.slug === params.slug);
  
  if (!project) {
    return <NotFound />;
  }

  usePageMeta(`${project.name} | Work`, project.summary);

  const relatedProjects = projects
    .filter(p => p.slug !== project.slug && p.categories.some(c => project.categories.includes(c)))
    .slice(0, 2);

  return (
    <Layout>
      <article className="container mx-auto px-6 max-w-4xl py-12 md:py-20">
        <Link href="/work" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground mb-12 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to all work
        </Link>
        
        <header className="mb-16">
          <div className="flex flex-wrap gap-3 mb-6">
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground">
              {project.industry}
            </Badge>
            {project.confidential && (
              <Badge variant="outline" className="border-accent text-accent">
                Confidential Project
              </Badge>
            )}
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium tracking-tight mb-6">
            {project.name}
          </h1>
          
          <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
            {project.summary}
          </p>
        </header>

        <div className={`aspect-[16/9] w-full rounded-2xl border border-border mb-16 flex items-center justify-center overflow-hidden shadow-sm ${project.imageStyle === 'cover' || project.imageStyle === 'contain-dark' ? 'bg-zinc-900' : 'bg-white dark:bg-zinc-800'}`}>
          {project.image ? (
            <img
              src={project.image}
              alt={project.imageAlt}
              className={`w-full h-full ${project.imageStyle === 'cover' ? 'object-cover' : 'object-contain p-12'}`}
            />
          ) : (
            <span className="text-muted-foreground/40 font-serif italic">[{project.imageAlt}]</span>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
          <div className="md:col-span-4 space-y-8">
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 border-b border-border pb-2">Role</h3>
              <p className="font-medium">{project.role}</p>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 border-b border-border pb-2">Core Areas</h3>
              <ul className="space-y-2">
                {project.areas.map(area => (
                  <li key={area} className="text-foreground/80">{area}</li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-3 border-b border-border pb-2">Technologies</h3>
              <ul className="space-y-2">
                {project.technologies.map(tech => (
                  <li key={tech} className="text-foreground/80">{tech}</li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="md:col-span-8 prose prose-lg dark:prose-invert max-w-none">
            {project.confidential && (
              <div className="bg-secondary/50 border-l-4 border-accent p-6 rounded-r-lg mb-10 not-prose">
                <p className="text-sm text-muted-foreground italic m-0">
                  <strong>Confidentiality Note:</strong> The specifics of this system, proprietary workflows, and financial metrics are private. This case study focuses on the structural complexity, architectural decisions, and operational execution.
                </p>
              </div>
            )}
            
            <h2 className="font-serif font-medium text-2xl mt-0">The Challenge</h2>
            <p className="text-muted-foreground leading-relaxed mb-10">{project.challenge}</p>
            
            <h2 className="font-serif font-medium text-2xl">What I Built</h2>
            <p className="text-muted-foreground leading-relaxed mb-10">{project.whatIBuilt}</p>
            
            <h2 className="font-serif font-medium text-2xl">The Outcome</h2>
            <p className="text-muted-foreground leading-relaxed mb-10">{project.outcome}</p>
          </div>
        </div>
      </article>
      
      {relatedProjects.length > 0 && (
        <section className="border-t border-border bg-card py-24">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-2xl font-serif font-medium mb-10">Related Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {relatedProjects.map(rp => (
                <Link key={rp.slug} href={`/work/${rp.slug}`}>
                  <div className="group p-6 rounded-2xl bg-background border border-border hover:border-accent/30 hover:shadow-sm transition-all cursor-pointer h-full flex flex-col">
                    <h3 className="text-xl font-serif font-medium mb-3 group-hover:text-accent transition-colors">{rp.name}</h3>
                    <p className="text-sm text-muted-foreground mb-6 flex-1">{rp.summary}</p>
                    <div className="flex items-center text-sm font-medium text-foreground">
                      View Project <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
